/**
 * The Who's coming answers as the page holds them, read from the step's JSON,
 * and the words a family sees for them.
 *
 * The page saves the whole object it holds. The server merges objects into
 * what it has but replaces arrays, so every key read here, known or not, is
 * kept as it came: above all each new child's studentEnrollmentId, which is how
 * the server knows a sibling it already added.
 */

export interface ChildAnswer {
  coming: boolean | null;
  tuitionOptionKey: string | null;
  [key: string]: unknown;
}

export interface NewChildAnswer {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gradeLevel: string;
  tuitionOptionKey: string | null;
  studentEnrollmentId?: string;
  [key: string]: unknown;
}

export interface WhoAnswers {
  children: Record<string, ChildAnswer>;
  paymentOptionKey: string | null;
  aidRequested: boolean;
  newChildren: NewChildAnswer[];
  [key: string]: unknown;
}

/** A program (tuition option) or a payment plan, by its key and name. */
export interface PriceOption {
  key: string;
  name: string;
  costCents?: number;
  description?: string;
}

type Raw = Record<string, unknown>;

function isRaw(value: unknown): value is Raw {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const text = (value: unknown) => (typeof value === 'string' ? value : '');
const key = (value: unknown) => (typeof value === 'string' && value !== '' ? value : null);

function readChild(value: unknown): ChildAnswer {
  const raw = isRaw(value) ? value : {};
  return {
    ...raw,
    coming: typeof raw.coming === 'boolean' ? raw.coming : null,
    tuitionOptionKey: key(raw.tuitionOptionKey),
  };
}

function readNewChild(raw: Raw): NewChildAnswer {
  return {
    ...raw,
    firstName: text(raw.firstName),
    lastName: text(raw.lastName),
    dateOfBirth: text(raw.dateOfBirth),
    gradeLevel: text(raw.gradeLevel),
    tuitionOptionKey: key(raw.tuitionOptionKey),
  };
}

export function readWhoAnswers(value: unknown): WhoAnswers {
  const raw = isRaw(value) ? value : {};
  const children = isRaw(raw.children) ? raw.children : {};
  return {
    ...raw,
    children: Object.fromEntries(Object.entries(children).map(([id, answer]) => [id, readChild(answer)])),
    paymentOptionKey: key(raw.paymentOptionKey),
    aidRequested: raw.aidRequested === true,
    newChildren: Array.isArray(raw.newChildren) ? raw.newChildren.filter(isRaw).map(readNewChild) : [],
  };
}

export const NEW_CHILD: NewChildAnswer = {
  firstName: '', lastName: '', dateOfBirth: '', gradeLevel: '', tuitionOptionKey: null,
};

/** Programs and plans arrive as the school's settings; only named ones are offered. */
export function readOptions(value: unknown): PriceOption[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRaw).flatMap((raw) => {
    if (typeof raw.key !== 'string' || raw.key === '' || typeof raw.name !== 'string' || raw.name.trim() === '') return [];
    return [{
      key: raw.key,
      name: raw.name,
      costCents: typeof raw.costCents === 'number' ? raw.costCents : undefined,
      description: typeof raw.description === 'string' && raw.description.trim() !== '' ? raw.description : undefined,
    }];
  });
}

const wholeDollars = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
});

/** "Full day - $9,000": the way the enrollment form has always shown families a program. */
export function programLabel(program: PriceOption): string {
  return typeof program.costCents === 'number'
    ? `${program.name} - ${wholeDollars.format(program.costCents / 100)}`
    : program.name;
}

export function optionName(options: PriceOption[], optionKey: string | null | undefined): string | null {
  return options.find((option) => option.key === optionKey)?.name ?? null;
}

/** The yes and no a family picks between for a child. */
export function comingWords(returning: boolean): { yes: string; no: string } {
  return returning ? { yes: 'Coming back', no: 'Not coming back' } : { yes: 'Coming', no: 'Not coming' };
}

/** What was answered for a child, e.g. "Coming back · Full day". */
export function answerWords(returning: boolean, answer: ChildAnswer | undefined, programs: PriceOption[]): string {
  const { yes, no } = comingWords(returning);
  if (answer?.coming === false) return no;
  if (answer?.coming !== true) return 'No answer yet';
  const program = optionName(programs, answer.tuitionOptionKey);
  return program ? `${yes} · ${program}` : yes;
}

export function newChildName(entry: NewChildAnswer): string {
  return `${entry.firstName.trim()} ${entry.lastName.trim()}`.trim();
}
